import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData, ImageFileUpload } from "../../../utility/Utility";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";

export default function UpdateUserPolicy() {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [formData, setFormData] = useState({
    userName: "",
    policyName: "",
    policyType: "",
    email: "",
    phone: "",
    price: "",
    documentImage: null,
    status: "active",
    existingImage: ""
  });

  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState("");

  useEffect(() => {
    if (state?.policyData) {
      const {
        userName,
        policyName,
        policyType,
        email,
        phone,
        price,
        documentImage,
        status
      } = state.policyData;

      setFormData({
        userName: userName || "",
        policyName: policyName || "",
        policyType: policyType || "",
        email: email || "",
        phone: phone || "",
        price: price || "",
        documentImage: null,
        status: status || "active",
        existingImage: documentImage || ""
      });

      if (documentImage) {
        setImagePreview(documentImage);
      }
    }
  }, [state]);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "documentImage" && files && files[0]) {
      const file = files[0];
      setFormData(prev => ({ ...prev, [name]: file }));

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const toggleStatus = () => {
    setFormData(prev => ({
      ...prev,
      status: prev.status === "active" ? "inactive" : "active"
    }));
  };

  const uploadImage = async () => {
    if (!formData.documentImage) return formData.existingImage;

    const imageFormData = new FormData();
    imageFormData.append("file", formData.documentImage);

    try {
      const response = await ImageFileUpload("api/admin/upload?is_upload=s3", imageFormData);
      if (response?.success) {
        return response?.url || response.data?.imageUrl || response.data?.filePath;
      } else {
        toast.error(response?.message || "Image upload failed");
        return null;
      }
    } catch (error) {
      toast.error("Network error during image upload");
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Upload image if a new one was selected
      let imageUrl = formData.existingImage;
      if (formData.documentImage) {
        const uploadedImageUrl = await uploadImage();
        if (!uploadedImageUrl) {
          setLoading(false);
          return;
        }
        imageUrl = uploadedImageUrl;
      }

      const payload = {
        userName: formData.userName,
        policyName: formData.policyName,
        policyType: formData.policyType,
        email: formData.email,
        phone: formData.phone,
        price: formData.price,
        documentImage: imageUrl,
        status: formData.status,
        id: state?.policyData?._id
      };

      const response = await postData("api/policies/update", payload);

      if (response.status) {
        toast.success("Policy updated successfully!");
        navigate(-1);
      } else {
        toast.error(response.message || "Failed to update policy");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
      console.error("Update policy error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-page">
      <CustomLoader loading={loading} />
      <ToastContainer position="top-right" autoClose={5000} />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <h4 className="fs-18 fw-semibold m-0">Policy Management</h4>
              <p className="fw-semibold my-2">Update Policy</p>
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              <i className="fas fa-arrow-left me-1"></i> Back
            </button>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">Policy Details</h5>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    {/* User Name */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label className="form-label fw-medium">
                          User Name <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="text"
                          name="userName"
                          className="form-control"
                          value={formData.userName}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>

                    {/* Policy Name */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label className="form-label fw-medium">
                          Policy Name <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="text"
                          name="policyName"
                          className="form-control"
                          value={formData.policyName}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>

                    {/* Policy Type */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label className="form-label fw-medium">
                          Policy Type <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <select
                          name="policyType"
                          className="form-select"
                          value={formData.policyType}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select Policy Type</option>
                          <option value="health">Health</option>
                          <option value="life">Life</option>
                          <option value="auto">Auto</option>
                          <option value="home">Home</option>
                        </select>
                      </div>
                    </div>

                    {/* Email */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label className="form-label fw-medium">
                          Email <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="email"
                          name="email"
                          className="form-control"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label className="form-label fw-medium">
                          Phone <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="tel"
                          name="phone"
                          className="form-control"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>

                    {/* Price */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label className="form-label fw-medium">
                          Price <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="number"
                          name="price"
                          className="form-control"
                          value={formData.price}
                          onChange={handleInputChange}
                          required
                          min="0"
                        />
                      </div>
                    </div>

                    {/* Document Image */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label className="form-label fw-medium">
                          Document Image
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="file"
                          name="documentImage"
                          className="form-control"
                          onChange={handleInputChange}
                          accept="image/*"
                        />
                        <small className="text-muted d-block mt-1">
                          Max 2MB (JPEG/PNG/PDF)
                        </small>
                        {imagePreview && (
                          <div className="mt-2">
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="img-thumbnail"
                              style={{ maxWidth: "200px", maxHeight: "150px" }}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Status */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label className="form-label fw-medium">Status</label>
                      </div>
                      <div className="col-md-6">
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="statusSwitch"
                            checked={formData.status === "active"}
                            onChange={toggleStatus}
                          />
                          <label className="form-check-label" htmlFor="statusSwitch">
                            {formData.status === "active" ? "Active" : "Inactive"}
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="row mb-3">
                      <div className="col-md-9 offset-md-3">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? (
                            <span className="spinner-border spinner-border-sm me-1"></span>
                          ) : (
                            <i className="mdi mdi-content-save me-1"></i>
                          )}
                          Update Policy
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}